import { useEffect, useState } from 'react';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Ban,
  Unlock,
  Zap,
  MoreVertical,
  X,
  AlertCircle,
  CheckCircle,
  Crown,
  Trash2,
  RotateCcw,
  FileText,
  Calendar,
  Award,
} from 'lucide-react';
import useAdminStore from '../../store/adminStore';
import { toast } from 'react-toastify';

const UserManagement = () => {
  const {
    users,
    usersTotal,
    usersPage,
    loading,
    fetchUsers,
    banUser,
    unbanUser,
    updateUserDP,
    deleteUser,
    togglePremium,
    getUserSubmissions,
    resetUserProgress,
  } = useAdminStore();

  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalType, setModalType] = useState(null); // 'ban', 'dp', 'view', 'delete', 'premium', 'submissions', 'reset'
  const [banReason, setBanReason] = useState('');
  const [dpAmount, setDpAmount] = useState('');
  const [premiumDuration, setPremiumDuration] = useState('1year');
  const [processing, setProcessing] = useState(false);
  const [userSubmissions, setUserSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [filterPremium, setFilterPremium] = useState('');

  const pageSize = 20;
  const totalPages = Math.ceil(usersTotal / pageSize);

  useEffect(() => {
    fetchUsers({ page: 1, limit: pageSize });
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers({ page: 1, limit: pageSize, search, premium: filterPremium || undefined });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchUsers({ page: newPage, limit: pageSize, search, premium: filterPremium || undefined });
    }
  };

  const handleBan = async () => {
    if (!selectedUser || !banReason.trim()) return;
    setProcessing(true);
    try {
      await banUser(selectedUser.id, banReason);
      toast.success(`User ${selectedUser.username} has been banned`);
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to ban user');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnban = async (user) => {
    if (!confirm(`Unban user ${user.username}?`)) return;
    try {
      await unbanUser(user.id);
      toast.success(`User ${user.username} has been unbanned`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to unban user');
    }
  };

  const handleUpdateDP = async () => {
    if (!selectedUser || !dpAmount) return;
    setProcessing(true);
    try {
      await updateUserDP(selectedUser.id, parseInt(dpAmount));
      toast.success(`Updated ${selectedUser.username}'s DP to ${dpAmount}`);
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update DP');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setProcessing(true);
    try {
      await deleteUser(selectedUser.id);
      toast.success(`User ${selectedUser.username} has been permanently deleted`);
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setProcessing(false);
    }
  };

  const handleTogglePremium = async (enable) => {
    if (!selectedUser) return;
    setProcessing(true);
    try {
      await togglePremium(selectedUser.id, enable, premiumDuration);
      toast.success(`${enable ? 'Granted' : 'Revoked'} premium for ${selectedUser.username}`);
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update premium status');
    } finally {
      setProcessing(false);
    }
  };

  const handleResetProgress = async () => {
    if (!selectedUser) return;
    setProcessing(true);
    try {
      await resetUserProgress(selectedUser.id);
      toast.success(`Progress reset for ${selectedUser.username}`);
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset progress');
    } finally {
      setProcessing(false);
    }
  };

  const loadUserSubmissions = async (user) => {
    setSubmissionsLoading(true);
    try {
      const data = await getUserSubmissions(user.id);
      setUserSubmissions(data.data || []);
    } catch (err) {
      toast.error('Failed to load submissions');
      setUserSubmissions([]);
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const openModal = (user, type) => {
    setSelectedUser(user);
    setModalType(type);
    if (type === 'dp') {
      setDpAmount(user.design_points?.toString() || '0');
    }
    if (type === 'ban') {
      setBanReason('');
    }
    if (type === 'premium') {
      setPremiumDuration('1year');
    }
    if (type === 'submissions') {
      loadUserSubmissions(user);
    }
  };

  const closeModal = () => {
    setSelectedUser(null);
    setModalType(null);
    setBanReason('');
    setDpAmount('');
    setUserSubmissions([]);
  };

  const isPremiumActive = (user) => {
    if (!user.is_premium) return false;
    if (!user.premium_expires_at) return true;
    return new Date(user.premium_expires_at) > new Date();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">User Management</h2>
          <p className="text-gray-500">Manage platform users, premium status, and access</p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <form onSubmit={handleSearch} className="flex gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by username or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={filterPremium}
            onChange={(e) => setFilterPremium(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Users</option>
            <option value="true">Premium Only</option>
            <option value="false">Free Only</option>
          </select>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Search
          </button>
        </form>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Premium</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DP</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                          {user.username?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{user.username}</div>
                          <div className="text-sm text-gray-500">ID: {user.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-900">{user.email}</span>
                        {user.email_verified ? (
                          <CheckCircle className="w-4 h-4 text-green-500" title="Verified" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-yellow-500" title="Unverified" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {user.is_banned ? (
                        <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                          Banned
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {isPremiumActive(user) ? (
                        <div className="flex items-center gap-1">
                          <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                            <Crown className="w-3 h-3" />
                            Premium
                          </span>
                          {user.premium_expires_at && (
                            <span className="text-xs text-gray-500">
                              (exp: {new Date(user.premium_expires_at).toLocaleDateString()})
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                          Free
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <Zap className="w-4 h-4 text-orange-500" />
                        <span className="font-medium">{user.design_points || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Premium Toggle */}
                        <button
                          onClick={() => openModal(user, 'premium')}
                          className={`p-2 rounded-lg ${
                            isPremiumActive(user)
                              ? 'text-amber-600 hover:bg-amber-50'
                              : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                          }`}
                          title="Toggle Premium"
                        >
                          <Crown className="w-5 h-5" />
                        </button>
                        {/* Adjust DP */}
                        <button
                          onClick={() => openModal(user, 'dp')}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Adjust DP"
                        >
                          <Zap className="w-5 h-5" />
                        </button>
                        {/* View Submissions */}
                        <button
                          onClick={() => openModal(user, 'submissions')}
                          className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                          title="View Submissions"
                        >
                          <FileText className="w-5 h-5" />
                        </button>
                        {/* Reset Progress */}
                        <button
                          onClick={() => openModal(user, 'reset')}
                          className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg"
                          title="Reset Progress"
                        >
                          <RotateCcw className="w-5 h-5" />
                        </button>
                        {/* Ban/Unban */}
                        {user.is_banned ? (
                          <button
                            onClick={() => handleUnban(user)}
                            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                            title="Unban"
                          >
                            <Unlock className="w-5 h-5" />
                          </button>
                        ) : (
                          <button
                            onClick={() => openModal(user, 'ban')}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title="Ban"
                          >
                            <Ban className="w-5 h-5" />
                          </button>
                        )}
                        {/* Delete */}
                        <button
                          onClick={() => openModal(user, 'delete')}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete User"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        {/* View Details */}
                        <button
                          onClick={() => openModal(user, 'view')}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                          title="View Details"
                        >
                          <MoreVertical className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {((usersPage - 1) * pageSize) + 1} to {Math.min(usersPage * pageSize, usersTotal)} of {usersTotal} users
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(usersPage - 1)}
                disabled={usersPage === 1}
                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="px-4 py-2 text-sm">
                Page {usersPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(usersPage + 1)}
                disabled={usersPage === totalPages}
                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalType && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`bg-white rounded-lg shadow-xl mx-4 ${modalType === 'submissions' ? 'max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col' : 'max-w-md w-full'}`}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">
                {modalType === 'ban' && 'Ban User'}
                {modalType === 'dp' && 'Adjust Design Points'}
                {modalType === 'view' && 'User Details'}
                {modalType === 'delete' && 'Delete User'}
                {modalType === 'premium' && 'Manage Premium'}
                {modalType === 'submissions' && 'User Submissions'}
                {modalType === 'reset' && 'Reset Progress'}
              </h3>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className={`p-6 ${modalType === 'submissions' ? 'overflow-y-auto flex-1' : ''}`}>
              {/* Ban Modal */}
              {modalType === 'ban' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                    <Ban className="w-6 h-6 text-red-500" />
                    <div>
                      <div className="font-medium text-red-800">{selectedUser.username}</div>
                      <div className="text-sm text-red-600">{selectedUser.email}</div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reason for ban
                    </label>
                    <textarea
                      value={banReason}
                      onChange={(e) => setBanReason(e.target.value)}
                      rows={3}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500"
                      placeholder="Enter reason for banning this user..."
                    />
                  </div>
                  <button
                    onClick={handleBan}
                    disabled={!banReason.trim() || processing}
                    className="w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {processing ? 'Processing...' : 'Ban User'}
                  </button>
                </div>
              )}

              {/* DP Modal */}
              {modalType === 'dp' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <Zap className="w-6 h-6 text-orange-500" />
                    <div>
                      <div className="font-medium text-gray-800">{selectedUser.username}</div>
                      <div className="text-sm text-gray-600">Current DP: {selectedUser.design_points || 0}</div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Design Points
                    </label>
                    <input
                      type="number"
                      value={dpAmount}
                      onChange={(e) => setDpAmount(e.target.value)}
                      min="0"
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    onClick={handleUpdateDP}
                    disabled={!dpAmount || processing}
                    className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {processing ? 'Processing...' : 'Update DP'}
                  </button>
                </div>
              )}

              {/* Delete Modal */}
              {modalType === 'delete' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                    <Trash2 className="w-6 h-6 text-red-500" />
                    <div>
                      <div className="font-medium text-red-800">{selectedUser.username}</div>
                      <div className="text-sm text-red-600">{selectedUser.email}</div>
                    </div>
                  </div>
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Warning:</strong> This action is permanent and cannot be undone. All user data including submissions, progress, and achievements will be permanently deleted.
                    </p>
                  </div>
                  <button
                    onClick={handleDelete}
                    disabled={processing}
                    className="w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {processing ? 'Deleting...' : 'Permanently Delete User'}
                  </button>
                </div>
              )}

              {/* Premium Modal */}
              {modalType === 'premium' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                    <Crown className="w-6 h-6 text-amber-500" />
                    <div>
                      <div className="font-medium text-gray-800">{selectedUser.username}</div>
                      <div className="text-sm text-gray-600">
                        {isPremiumActive(selectedUser) ? 'Currently Premium' : 'Free User'}
                        {selectedUser.premium_expires_at && (
                          <span className="ml-2">(expires: {new Date(selectedUser.premium_expires_at).toLocaleDateString()})</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {!isPremiumActive(selectedUser) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Premium Duration
                      </label>
                      <select
                        value={premiumDuration}
                        onChange={(e) => setPremiumDuration(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="1month">1 Month</option>
                        <option value="3months">3 Months</option>
                        <option value="6months">6 Months</option>
                        <option value="1year">1 Year</option>
                        <option value="lifetime">Lifetime</option>
                      </select>
                    </div>
                  )}

                  <div className="flex gap-3">
                    {isPremiumActive(selectedUser) ? (
                      <button
                        onClick={() => handleTogglePremium(false)}
                        disabled={processing}
                        className="flex-1 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
                      >
                        {processing ? 'Processing...' : 'Revoke Premium'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleTogglePremium(true)}
                        disabled={processing}
                        className="flex-1 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                      >
                        {processing ? 'Processing...' : 'Grant Premium'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Reset Progress Modal */}
              {modalType === 'reset' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                    <RotateCcw className="w-6 h-6 text-orange-500" />
                    <div>
                      <div className="font-medium text-gray-800">{selectedUser.username}</div>
                      <div className="text-sm text-gray-600">Reset all submissions and challenge progress</div>
                    </div>
                  </div>
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Warning:</strong> This will delete all submissions and reset challenge progress. Achievements will be preserved.
                    </p>
                  </div>
                  <button
                    onClick={handleResetProgress}
                    disabled={processing}
                    className="w-full py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                  >
                    {processing ? 'Resetting...' : 'Reset Progress'}
                  </button>
                </div>
              )}

              {/* Submissions Modal */}
              {modalType === 'submissions' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                    <FileText className="w-6 h-6 text-purple-500" />
                    <div>
                      <div className="font-medium text-gray-800">{selectedUser.username}</div>
                      <div className="text-sm text-gray-600">Submission History</div>
                    </div>
                  </div>

                  {submissionsLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : userSubmissions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No submissions found
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Challenge</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Difficulty</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Score</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Grade</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {userSubmissions.map((sub) => (
                            <tr key={sub.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 font-medium">{sub.challenge_title}</td>
                              <td className="px-3 py-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs ${
                                  sub.difficulty === 'beginner' ? 'bg-green-100 text-green-700' :
                                  sub.difficulty === 'intermediate' ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {sub.difficulty}
                                </span>
                              </td>
                              <td className="px-3 py-2">{sub.final_score}</td>
                              <td className="px-3 py-2">
                                <span className={`px-2 py-0.5 rounded font-bold ${
                                  sub.grade === 'S' ? 'bg-purple-100 text-purple-700' :
                                  sub.grade === 'A' ? 'bg-green-100 text-green-700' :
                                  sub.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                                  sub.grade === 'C' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {sub.grade || 'N/A'}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-gray-500">
                                {new Date(sub.submitted_at).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* View Modal */}
              {modalType === 'view' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold">
                      {selectedUser.username?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        {selectedUser.username}
                        {isPremiumActive(selectedUser) && <Crown className="w-5 h-5 text-amber-500" />}
                      </div>
                      <div className="text-gray-500">{selectedUser.email}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <div className="text-sm text-gray-500">User ID</div>
                      <div className="font-medium">{selectedUser.id}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Design Points</div>
                      <div className="font-medium">{selectedUser.design_points || 0}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Total Score</div>
                      <div className="font-medium">{selectedUser.total_score || 0}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Email Verified</div>
                      <div className="font-medium">{selectedUser.email_verified ? 'Yes' : 'No'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Account Status</div>
                      <div className="font-medium">{selectedUser.is_banned ? 'Banned' : 'Active'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Premium Status</div>
                      <div className="font-medium">{isPremiumActive(selectedUser) ? 'Premium' : 'Free'}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-sm text-gray-500">Joined</div>
                      <div className="font-medium">{new Date(selectedUser.created_at).toLocaleString()}</div>
                    </div>
                    {selectedUser.premium_expires_at && (
                      <div className="col-span-2">
                        <div className="text-sm text-gray-500">Premium Expires</div>
                        <div className="font-medium">{new Date(selectedUser.premium_expires_at).toLocaleString()}</div>
                      </div>
                    )}
                    {selectedUser.is_banned && (
                      <div className="col-span-2">
                        <div className="text-sm text-gray-500">Ban Reason</div>
                        <div className="font-medium text-red-600">{selectedUser.ban_reason || 'No reason provided'}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
